import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';

const CompareContext = createContext(null);

export const useCompare = () => {
    const context = useContext(CompareContext);
    if (!context) {
        throw new Error('useCompare must be used within CompareProvider');
    }
    return context;
};

export const CompareProvider = ({ children }) => {
    const [compareList, setCompareList] = useState([]);
    const MAX_COMPARE_ITEMS = 4;

    const addToCompare = useCallback((product) => {
        setCompareList(prev => {
            if (prev.find(p => p.product_id === product.product_id)) {
                return prev; // Already in list
            }
            if (prev.length >= MAX_COMPARE_ITEMS) {
                return prev; // Max reached
            }
            return [...prev, product];
        });
    }, []);

    const removeFromCompare = useCallback((productId) => {
        setCompareList(prev => prev.filter(p => p.product_id !== productId));
    }, []);

    const isInCompare = useCallback((productId) => {
        return compareList.some(p => p.product_id === productId);
    }, [compareList]);

    const clearCompare = useCallback(() => {
        setCompareList([]);
    }, []);

    const canAddMore = compareList.length < MAX_COMPARE_ITEMS;

    const contextValue = useMemo(() => ({
        compareList,
        addToCompare,
        removeFromCompare,
        isInCompare,
        clearCompare,
        canAddMore,
        compareCount: compareList.length,
        maxItems: MAX_COMPARE_ITEMS
    }), [compareList, addToCompare, removeFromCompare, isInCompare, clearCompare, canAddMore]);

    return (
        <CompareContext.Provider value={contextValue}>
            {children}
        </CompareContext.Provider>
    );
};
